// src/helper/formatPartCode.js

/**
 * Always format to: {organizationName}{sep}{categoryCode}{sep}{unique_num}{sep}{revision_num}
 * Where sep = options.separator (default "-")
 *
 * Supported calls per requirement:
 * - formatPartCode(part)                               -> from part.unique_num, part.partCategory.categoryCode, part.revision_num
 * - formatPartCode(part, partCategory)                 -> from part.unique_num, partCategory.categoryCode, part.revision_num
 * - formatPartCode(unique_num, categoryCode, revision) -> use the three pieces directly
 * - formatPartCode(inventoryItem|identifier)           -> identifier already formatted with '-' separator; only pad unique/rev if shorter
 * An optional final options object can be provided in all cases.
 */

/**
 * @typedef {Object} FormatOptions
 * @property {string} [separator='-']               - Separator between segments.
 * @property {boolean} [uppercaseCategory=true]     - Uppercase the category segment.
 * @property {boolean} [uppercaseRevision=true]     - Uppercase the revision segment.
 * @property {number} [uniquePad=5]                 - Left-pad unique_num (digits only) to this width with zeros.
 * @property {number} [revisionPad=2]               - Left-pad revision_num (digits only) to this width with zeros.
 * @property {boolean} [trimWhitespace=true]        - Trim and remove inner whitespace.
 * @property {boolean} [collapseSeparators=true]    - Collapse multiple separators to one.
 * @property {string} [allowedExtra='._']           - Extra allowed non-alphanumeric chars within segments.
 * @property {string} [organizationName='E4A']      - Organization prefix to always apply.
 */

const DEFAULTS = {
    organizationName: 'E4A',
    separator: '-',
    uppercaseCategory: true,
    uppercaseRevision: true,
    // Note: uppercaseCode no longer used; kept for backward-compat with options shapes
    uppercaseCode: false,
    uniquePad: 5,
    revisionPad: 2,
    trimWhitespace: true,
    collapseSeparators: true,
    allowedExtra: '._',
};

const isNil = (v) => v === null || v === undefined;

function toStr(v) {
    if (isNil(v)) return '';
    return String(v);
}

function escapeForCharClass(s) {
    return toStr(s).replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
}

function scrubSegment(raw, { trimWhitespace, collapseSeparators, allowedExtra }, toUpper = false) {
    let s = toStr(raw);
    if (trimWhitespace) s = s.trim();
    // Remove inner whitespace
    s = s.replace(/\s+/g, '');
    // Keep only alphanumerics and allowed extras and hyphen
    const safeRe = new RegExp(`[^A-Za-z0-9\\-${escapeForCharClass(allowedExtra)}]+`, 'g');
    s = s.replace(safeRe, '');
    if (collapseSeparators) s = s.replace(/-+/g, '-');
    if (toUpper) s = s.toUpperCase();
    return s;
}

function padIfNumeric(s, width) {
    if (!width || width <= 0) return s;
    if (/^\d+$/.test(s)) return s.padStart(width, '0');
    return s;
}

// If input is an inventoryItem or an identifier string that is already a dashed partcode,
// only pad unique_num and revision_num if they are shorter than the configured widths.
// If lengths are equal or greater, leave as-is and return the identifier unchanged.
function normalizeIdentifierIfNeeded(input, opts) {
    const sep = '-'; // per requirement, identifiers use a dash as separator
    const process = (s) => {
        if (typeof s !== 'string' || !s.includes(sep)) return null;
        const parts = s.split(sep);
        // Need at least CATEGORY-UNIQUE-REVISION (3 parts). If ORG present, it'll be 4 parts.
        if (parts.length < 3) return null;

        const uIdx = parts.length - 2; // unique segment index (2 for 4-part, 1 for 3-part)
        const rIdx = parts.length - 1; // revision segment index (3 for 4-part, 2 for 3-part)

        const origU = parts[uIdx] ?? '';
        const origR = parts[rIdx] ?? '';

        const newU = padIfNumeric(origU, opts.uniquePad);
        const newR = padIfNumeric(origR, opts.revisionPad);

        if (newU === origU && newR === origR) return s; // nothing to change
        parts[uIdx] = newU;
        parts[rIdx] = newR;
        return parts.join(sep);
    };

    if (input && typeof input === 'object' && typeof input.identifier === 'string') {
        return process(input.identifier);
    }
    if (typeof input === 'string') {
        return process(input);
    }
    return null;
}

function prefixWithOrg(code, opts) {
    const s = toStr(code);
    if (!s) return s;
    const sep = toStr(!isNil(opts.separator) ? opts.separator : DEFAULTS.separator);
    const prefix = `${opts && opts.organizationName ? opts.organizationName : DEFAULTS.organizationName}${sep}`;
    if (s.toUpperCase().startsWith(prefix.toUpperCase())) return s;
    return prefix + s;
}

/**
 * Main formatter
 * Always returns: ORG{sep}CATEGORY{sep}UNIQUE{sep}REVISION
 * @param  {...any} args
 * @returns {string}
 */
export function formatPartCode(...args) {
    if (args.length === 0) return '';

    // Extract optional options (last arg as plain object not containing data keys)
    let options = {};
    if (args.length >= 1) {
        const last = args[args.length - 1];
        if (isPlainOptions(last)) {
            options = last;
            args = args.slice(0, -1);
        }
    }
    const opts = { ...DEFAULTS, ...options };

    // New behavior: if input is inventoryItem or identifier string (already formatted with "-"),
    // only pad unique/revision if shorter; otherwise return identifier as-is.
    if (args.length === 1) {
        const normalized = normalizeIdentifierIfNeeded(args[0], opts);
        if (typeof normalized === 'string') return normalized;
    }

    let unique_num = '';
    let categoryCode = '';
    let revision_num = '';

    if (args.length === 1) {
        // Single arg: part object
        const part = args[0];
        if (isPartObject(part)) {
            unique_num = toStr(part.unique_num);
            categoryCode = toStr(getNestedCategoryCode(part) || part.categoryCode);
            revision_num = toStr(part.revision_num);
        } else {
            // Not supported per the new contract; return an empty skeleton with org prefix
            const formatted = buildFromPieces('', '', '', opts);
            return prefixWithOrg(formatted, opts);
        }
    } else if (args.length === 2) {
        // Two args: (part, partCategory)
        const [part, partCategory] = args;
        unique_num = isPartObject(part) ? toStr(part.unique_num) : toStr(part);
        categoryCode = typeof partCategory === 'object'
            ? toStr(partCategory?.categoryCode || '')
            : toStr(partCategory);
        revision_num = isPartObject(part) ? toStr(part.revision_num) : '';
    } else {
        // Three or more args: (unique_num, categoryCode, revision_num)
        [unique_num, categoryCode, revision_num] = args;
        unique_num = toStr(unique_num);
        categoryCode = toStr(categoryCode);
        revision_num = toStr(revision_num);
    }

    const formatted = buildFromPieces(unique_num, categoryCode, revision_num, opts);
    return prefixWithOrg(formatted, opts);
}

function buildFromPieces(unique_num, categoryCode, revision_num, opts) {
    const category = scrubSegment(categoryCode, opts, opts.uppercaseCategory);

    const uniqueScrubbed = scrubSegment(unique_num, opts, false);
    const unique = padIfNumeric(uniqueScrubbed, opts.uniquePad);

    const revScrubbed = scrubSegment(revision_num, opts, opts.uppercaseRevision);
    const revision = padIfNumeric(revScrubbed, opts.revisionPad);

    // Always include all three segments (category, unique, revision) in order
    return [category, unique, revision].join(opts.separator);
}

function isPlainOptions(v) {
    if (!v || typeof v !== 'object') return false;
    const known = Object.keys(DEFAULTS);
    const hasOptionKey = known.some((k) => k in v);
    const hasDataKey =
        'categoryCode' in v ||
        'unique_num' in v ||
        'revision_num' in v ||
        'partCode' in v ||
        'partCategory' in v;
    return hasOptionKey && !hasDataKey;
}

function isPartObject(v) {
    return v && typeof v === 'object' && ('unique_num' in v || 'partCode' in v || 'partCategory' in v);
}

function getNestedCategoryCode(v) {
    try {
        return v && v.partCategory && v.partCategory.categoryCode ? v.partCategory.categoryCode : '';
    } catch {
        return '';
    }
}

export default formatPartCode;

/*
Examples per requirement:

formatPartCode(part);
// -> "E4A-{CATEGORY}-{UNIQUE}-{REVISION}" using part.partCategory.categoryCode, part.unique_num, part.revision_num

formatPartCode(part, partCategory);
// -> "E4A-{CATEGORY_FROM_PARTCATEGORY}-{part.unique_num}-{part.revision_num}"

formatPartCode(42, 'MECH', 7);
// -> "E4A-MECH-00042-07" (with default pads)

formatPartCode('E4A-MECH-12-3');
// -> "E4A-MECH-00012-03" (only pads if shorter)

formatPartCode({ identifier: 'E4A-ELEC-12345-12' });
// -> "E4A-ELEC-12345-12" (unchanged, equal or longer)
*/
